import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    const [useSystemDefault, setUseSystemDefault] = useState(() => {
        return localStorage.getItem('useSystemDefault') === 'true';
    });
    
    const [theme, setTheme] = useState(() => {
        // Check if user prefers system default
        const savedUseSystemDefault = localStorage.getItem('useSystemDefault') === 'true';
        
        if (savedUseSystemDefault) {
            // Use system preference
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        
        // Respect user's saved preference or default to 'light'
        const savedTheme = localStorage.getItem('theme');
        return savedTheme || 'light';
    });

    useEffect(() => {
        const root = window.document.documentElement;
        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        
        // Only save theme to localStorage if not using system default
        if (!useSystemDefault) {
            localStorage.setItem('theme', theme);
        }
    }, [theme, useSystemDefault]);

    // Listen for system theme changes when using system default
    useEffect(() => {
        if (!useSystemDefault) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            setTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [useSystemDefault]);

    // Update localStorage when useSystemDefault changes
    useEffect(() => {
        localStorage.setItem('useSystemDefault', useSystemDefault.toString());
    }, [useSystemDefault]);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    const toggleSystemDefault = () => {
        const newUseSystemDefault = !useSystemDefault;
        setUseSystemDefault(newUseSystemDefault);
        
        if (newUseSystemDefault) {
            // Switch to system theme immediately
            const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            setTheme(systemTheme);
        }
    };

    const setSystemDefaultEnabled = (enabled) => {
        setUseSystemDefault(enabled);
        
        if (enabled) {
            // Switch to system theme immediately
            const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            setTheme(systemTheme);
        }
    };

    return (
        <ThemeContext.Provider value={{ 
            theme, 
            toggleTheme, 
            useSystemDefault, 
            toggleSystemDefault,
            setSystemDefaultEnabled 
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
